// src/pages/OvertimeOverview.js

import React, { useEffect, useMemo, useState } from "react";
import LoadingIndicator from "../components/LoadingIndicator";
import {
  FaSearch,
  FaBusinessTime,
  FaRegClock,
  FaMoneyBillWave,
  FaUser,
} from "react-icons/fa";
import {
  collection,
  onSnapshot,
  query,
  where,
  doc,
  getDoc,
  getDocs,
  limit,
} from "firebase/firestore";
import { db } from "../firebase";
import { useAuth } from "../contexts/AuthContext";
import { useCompany } from "../contexts/CompanyContext";

const overtimeOverviewCss = `
.ot-person-toggle { display: flex; align-items: center; gap: 8px; width: 100%; border: 0; padding: 0; background: transparent; color: inherit; font: inherit; text-align: left; cursor: pointer; }
.ot-person-toggle:focus-visible, .ot-calculation summary:focus-visible { outline: 2px solid #c2410c; outline-offset: 5px; border-radius: 4px; }
.ot-person-heading { display: flex; flex-wrap: wrap; justify-content: space-between; gap: 8px; font-size: 15px; color: #1f2937; }
.ot-person-heading > span, .ot-muted, .ot-help, .ot-no-compensation { color: #5f6b7a; }
.ot-help { margin: 8px 0 16px; font-size: 13px; line-height: 1.6; }
.ot-shift-list { display: grid; gap: 12px; }
.ot-shift-card { background: white; border: 1px solid #dfe5ee; border-radius: 10px; overflow: hidden; }
.ot-shift-summary { display: grid; grid-template-columns: minmax(150px, 1.4fr) 1fr 1fr; align-items: center; gap: 16px; padding: 16px; font-size: 14px; color: #1f2937; }
.ot-muted { display: block; font-size: 12px; margin-bottom: 3px; }
.ot-shift-amount { text-align: right; }
.ot-shift-amount strong { color: #047857; font-size: 17px; }
.ot-calculation { border-top: 1px solid #edf0f5; }
.ot-calculation summary { padding: 10px 16px; cursor: pointer; color: #9a3412; font-weight: 600; }
.ot-hide, .ot-calculation[open] .ot-show { display: none; }
.ot-calculation[open] .ot-hide { display: inline; }
.ot-calculation-scroll { overflow-x: auto; padding: 0 16px; }
.ot-calculation-table { width: 100%; border-collapse: collapse; font-size: 13px; }
.ot-calculation-table caption { text-align: left; padding: 8px 0; font-weight: 600; }
.ot-calculation-table th, .ot-calculation-table td { padding: 10px 12px; text-align: left; border-bottom: 1px solid #edf0f5; }
.ot-calculation-table thead { background: #f4f6fa; color: #526075; }
.ot-calculation-table td { white-space: nowrap; font-variant-numeric: tabular-nums; }
.ot-calculation-table td:last-child, .ot-calculation-table thead th:last-child { text-align: right; }
.ot-calculation-table tfoot { font-weight: 700; color: #1f2937; }
.ot-calculation > .ot-help { padding: 0 16px; }
.ot-no-compensation { margin: 0; padding: 10px 16px; border-top: 1px solid #edf0f5; font-size: 13px; }
@media (max-width: 700px) {
  .ot-shift-summary { grid-template-columns: 1fr 1fr; gap: 12px; }
  .ot-shift-amount { text-align: left; }
}

.ot-detail-grid { display: grid; grid-template-columns: repeat(auto-fit, minmax(160px, 1fr)); gap: 16px; padding: 0 16px 16px; margin: 0; }
.ot-detail-grid dt { color: #5f6b7a; font-size: 12px; }
.ot-detail-grid dd { margin: 3px 0 0; color: #1f2937; }
`;

function safeParseDate(dateValue) {
  if (!dateValue) return null;

  try {
    if (dateValue instanceof Date) {
      return isNaN(dateValue.getTime()) ? null : dateValue;
    }

    if (typeof dateValue === "object" && typeof dateValue.toDate === "function") {
      const parsed = dateValue.toDate();
      return parsed instanceof Date && !isNaN(parsed.getTime()) ? parsed : null;
    }

    if (typeof dateValue === "string" || typeof dateValue === "number") {
      const parsed = new Date(dateValue);
      return isNaN(parsed.getTime()) ? null : parsed;
    }

    return null;
  } catch (error) {
    console.error("Fel vid datumtolkning:", error);
    return null;
  }
}

function getMonthIndexFromSwedish(monthName) {
  const months = {
    Januari: 0,
    Februari: 1,
    Mars: 2,
    April: 3,
    Maj: 4,
    Juni: 5,
    Juli: 6,
    Augusti: 7,
    September: 8,
    Oktober: 9,
    November: 10,
    December: 11,
  };

  return months[monthName] ?? 0;
}

function formatTimeDisplay(timeString) {
  if (!timeString) return "N/A";
  return String(timeString).substring(0, 5);
}

function formatDateDisplay(dateValue) {
  const date = safeParseDate(dateValue);
  if (!date) return "N/A";
  return date.toLocaleDateString("sv-SE");
}

const formatHours = (hours) =>
  Number(hours || 0).toLocaleString("sv-SE", {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  });

const formatCurrency = (amount) =>
  `${Number(amount || 0).toLocaleString("sv-SE", {
    minimumFractionDigits: 0,
    maximumFractionDigits: 0,
  })} kr`;

function getEmployeeName(employee) {
  if (!employee) return "Okänd anställd";
  if (employee.name) return employee.name;
  if (employee.firstName && employee.lastName) return `${employee.firstName} ${employee.lastName}`;
  if (employee.firstName) return employee.firstName;
  if (employee.lastName) return employee.lastName;
  if (employee.displayName) return employee.displayName;
  if (employee.userName) return employee.userName;
  return "Okänd anställd";
}

function getEmployeeIdFromRequest(request, employees = []) {
  if (!request) return null;

  if (request.employeeId) return request.employeeId;
  if (request.userId) return request.userId;
  if (request.uid) return request.uid;

  const email = String(request.userEmail || request.email || "")
    .trim()
    .toLowerCase();

  if (!email) return null;

  const employee = employees.find(
    (emp) => String(emp.email || "").trim().toLowerCase() === email
  );

  return employee?.id || null;
}

function getRequestDateRange(request) {
  const startDate =
    safeParseDate(request?.startDate) ||
    safeParseDate(request?.fromDate) ||
    safeParseDate(request?.dateFrom) ||
    safeParseDate(request?.overtimeStartDate) ||
    safeParseDate(request?.requestedStartDate) ||
    safeParseDate(request?.date) ||
    safeParseDate(request?.shiftDate) ||
    safeParseDate(request?.overtimeDate) ||
    safeParseDate(request?.requestedDate) ||
    null;

  const endDate =
    safeParseDate(request?.endDate) ||
    safeParseDate(request?.toDate) ||
    safeParseDate(request?.dateTo) ||
    safeParseDate(request?.overtimeEndDate) ||
    safeParseDate(request?.requestedEndDate) ||
    safeParseDate(request?.shiftEndDate) ||
    startDate;

  const fallbackDate =
    safeParseDate(request?.submittedAt) ||
    safeParseDate(request?.createdAt) ||
    safeParseDate(request?.approvedAt) ||
    null;

  const resolvedStart = startDate || endDate || fallbackDate;
  const resolvedEnd = endDate || startDate || fallbackDate;

  if (!resolvedStart || !resolvedEnd) {
    return { startDate: null, endDate: null };
  }

  if (resolvedEnd.getTime() < resolvedStart.getTime()) {
    return { startDate: resolvedEnd, endDate: resolvedStart };
  }

  return { startDate: resolvedStart, endDate: resolvedEnd };
}

function requestRangeOverlapsMonth(startDate, endDate, monthIndex, year) {
  if (!startDate || !endDate) return false;

  const monthStart = new Date(year, monthIndex, 1, 0, 0, 0, 0);
  const monthEnd = new Date(year, monthIndex + 1, 0, 23, 59, 59, 999);

  return startDate <= monthEnd && endDate >= monthStart;
}

function formatDateRangeDisplay(startDate, endDate) {
  if (!startDate && !endDate) return "N/A";
  if (!endDate || startDate?.toDateString() === endDate?.toDateString()) {
    return formatDateDisplay(startDate || endDate);
  }
  return `${formatDateDisplay(startDate)} - ${formatDateDisplay(endDate)}`;
}

function normalizeId(value) {
  return String(value ?? "").trim();
}

function getOvertimeRuleIdFromRequest(request) {
  return normalizeId(
    request?.overtimeRuleId ||
      request?.overtimeTypeId ||
      request?.ruleId ||
      request?.typeId ||
      request?.overtime?.overtimeRuleId ||
      ""
  );
}

function getOvertimePercentage(request, overtimeRules = []) {
  const overtimeRuleId = getOvertimeRuleIdFromRequest(request);

  const matchingRule = overtimeRuleId
    ? overtimeRules.find(
        (rule) => normalizeId(rule?.id) === normalizeId(overtimeRuleId)
      )
    : null;

  // Samma reservordning som TimeReportPopup:
  // 1. procent från ansökan
  // 2. matchad övertidsregel
  // 3. första giltiga regel i Firestores ursprungliga ordning
  // 4. 100 % endast när ingen regel alls kan användas
  const fallbackRule =
    matchingRule ||
    overtimeRules.find((rule) => Number(rule?.percentage || 0) > 0) ||
    null;

  return Number(
    request?.percentage ??
      request?.overtimePercentage ??
      request?.percent ??
      request?.ratePercent ??
      request?.overtime?.percentage ??
      fallbackRule?.percentage ??
      100
  );
}

function getOvertimeHours(request) {
  const minutes = Number(
    request?.minutes ??
      request?.overtimeMinutes ??
      request?.durationMinutes ??
      request?.approvedMinutes ??
      request?.overtime?.minutes ??
      0
  );

  return Number(
    request?.hours ??
      request?.overtimeHours ??
      request?.durationHours ??
      request?.approvedHours ??
      request?.totalHours ??
      request?.overtime?.hours ??
      (minutes > 0 ? minutes / 60 : 0)
  );
}

function getEmployeeOvertimeHourlyRate(employee) {
  const payroll = employee?.payroll || {};
  const payType =
    payroll?.draftPayType || payroll?.savedPayType || employee?.draftPayType || employee?.savedPayType || "hourly";
  const payAmount = Number(
    payroll?.draftPayAmount ||
      payroll?.savedPayAmount ||
      employee?.draftPayAmount ||
      employee?.savedPayAmount ||
      0
  );

  if (payType === "monthly") {
    return payAmount > 0 ? payAmount / 165 : 0;
  }

  return payAmount;
}


function timeToMinutes(timeString) {
  if (!timeString || typeof timeString !== "string") return 0;
  const [hours, minutes] = String(timeString).split(":").map(Number);
  if (Number.isNaN(hours) || Number.isNaN(minutes)) return 0;
  return hours * 60 + minutes;
}

function calculateTotalShiftMinutes(shift) {
  if (!shift?.startTime || !shift?.endTime) return 0;
  const start = timeToMinutes(shift.startTime);
  const end = timeToMinutes(shift.endTime);
  let minutes = end - start;
  if (minutes < 0) minutes += 24 * 60;
  return minutes;
}

function calculateTotalBreakMinutes(employeeData) {
  let total = 0;
  if (!Array.isArray(employeeData?.breaks)) return total;

  employeeData.breaks.forEach((breakItem) => {
    if (breakItem?.appliesToWholeShift) return;
    if (!breakItem?.startTime || !breakItem?.endTime) return;

    const start = timeToMinutes(breakItem.startTime);
    const end = timeToMinutes(breakItem.endTime);
    let minutes = end - start;
    if (minutes < 0) minutes += 24 * 60;
    total += minutes;
  });

  return total;
}

function calculateOvertimeForShift(shift, employeeData = null) {
  let totalOvertimeMinutes = 0;
  let premiumOvertimeMinutes = 0;
  let premiumValue = 0;

  if (!Array.isArray(employeeData?.overtime)) {
    return { hours: 0, premiumValue: 0, premiumHours: 0 };
  }

  employeeData.overtime.forEach((overtimeItem) => {
    let itemMinutes = 0;

    if (overtimeItem?.appliesToWholeShift) {
      itemMinutes = Math.max(
        0,
        calculateTotalShiftMinutes(shift) - calculateTotalBreakMinutes(employeeData)
      );
    } else if (overtimeItem?.startTime && overtimeItem?.endTime) {
      const start = timeToMinutes(overtimeItem.startTime);
      const end = timeToMinutes(overtimeItem.endTime);
      itemMinutes = end - start;
      if (itemMinutes < 0) itemMinutes += 24 * 60;
    } else if (Number(overtimeItem?.hours || 0) > 0) {
      itemMinutes = Number(overtimeItem.hours) * 60;
    }

    const percentage = Number(overtimeItem?.percentage || 0);
    totalOvertimeMinutes += itemMinutes;

    if (percentage > 0) {
      premiumOvertimeMinutes += itemMinutes;
      // premiumValue är redan timmar × procent.
      premiumValue += (itemMinutes / 60) * (percentage / 100);
    }
  });

  return {
    hours: totalOvertimeMinutes / 60,
    premiumHours: premiumOvertimeMinutes / 60,
    premiumValue,
  };
}

function getDateKey(value) {
  const parsed = safeParseDate(value);
  if (!parsed) return "";
  const month = String(parsed.getMonth() + 1).padStart(2, "0");
  const day = String(parsed.getDate()).padStart(2, "0");
  return `${parsed.getFullYear()}-${month}-${day}`;
}

function getShiftIdFromRequest(request) {
  return normalizeId(
    request?.shiftId ||
      request?.originalShiftId ||
      request?.scheduleShiftId ||
      request?.linkedShiftId ||
      request?.shift?.id ||
      request?.shift?.shiftId ||
      ""
  );
}

function getRequestDateKey(request) {
  return getDateKey(
    request?.date ||
      request?.shiftDate ||
      request?.overtimeDate ||
      request?.requestedDate ||
      request?.startDate ||
      request?.fromDate ||
      null
  );
}

function buildApprovedOvertimeEntry(request, overtimeRules = []) {
  const percentage = getOvertimePercentage(request, overtimeRules);
  const hours = getOvertimeHours(request);
  const ruleId = getOvertimeRuleIdFromRequest(request);
  const matchingRule = ruleId
    ? overtimeRules.find((rule) => normalizeId(rule?.id) === ruleId)
    : null;
  const fallbackRule =
    matchingRule ||
    overtimeRules.find((rule) => Number(rule?.percentage || 0) > 0) ||
    null;

  return {
    overtimeRequestId: request?.id,
    startTime:
      request?.startTime || request?.fromTime || request?.overtimeStartTime || "",
    endTime:
      request?.endTime || request?.toTime || request?.overtimeEndTime || "",
    appliesToWholeShift: Boolean(
      request?.appliesToWholeShift ?? request?.wholeShift ?? request?.isWholeShift
    ),
    overtimeRuleId: ruleId || fallbackRule?.id || "approved-request",
    overtimeRuleName:
      request?.overtimeRuleName ||
      request?.overtimeTypeName ||
      request?.ruleName ||
      request?.typeName ||
      matchingRule?.name ||
      fallbackRule?.name ||
      "Godkänd övertid",
    percentage,
    hours,
    approvedFromRequest: true,
  };
}

function mergeApprovedOvertimeForEmployee({
  shift,
  employeeId,
  employeeData,
  approvedOvertimeRequests,
  employees,
  overtimeRules,
}) {
  const shiftId = normalizeId(shift?.originalId || shift?.id);
  const shiftDateKey = getDateKey(shift?.date);

  const manualOvertime = Array.isArray(employeeData?.overtime)
    ? employeeData.overtime.filter((item) => !item?.approvedFromRequest)
    : [];

  const matchingRequests = (approvedOvertimeRequests || []).filter((request) => {
    const requestEmployeeId = getEmployeeIdFromRequest(request, employees);
    if (normalizeId(requestEmployeeId) !== normalizeId(employeeId)) return false;

    const requestShiftId = getShiftIdFromRequest(request);
    if (requestShiftId) return requestShiftId === shiftId;

    const requestDateKey = getRequestDateKey(request);
    return Boolean(requestDateKey && shiftDateKey && requestDateKey === shiftDateKey);
  });

  return {
    ...employeeData,
    overtime: [
      ...manualOvertime,
      ...matchingRequests.map((request) =>
        buildApprovedOvertimeEntry(request, overtimeRules)
      ),
    ],
  };
}

function buildEmployeeEntriesFromShift(
  shift,
  bookings = [],
  approvedOvertimeRequests = [],
  employees = [],
  overtimeRules = []
) {
  const entries = [];
  const added = new Set();

  const addEntry = (employeeId, employeeData = {}) => {
    const id = normalizeId(employeeId);
    if (!id || employeeData?.isObemannad || added.has(id)) return;
    added.add(id);

    const baseData = {
      breaks: employeeData?.breaks || [],
      overtime: employeeData?.overtime || [],
      absences: employeeData?.absences || [],
      ...employeeData,
    };

    entries.push({
      employeeId: id,
      employeeData: mergeApprovedOvertimeForEmployee({
        shift,
        employeeId: id,
        employeeData: baseData,
        approvedOvertimeRequests,
        employees,
        overtimeRules,
      }),
    });
  };

  if (Array.isArray(shift?.employees) && shift.employees.length > 0) {
    shift.employees.forEach((employeeData) =>
      addEntry(employeeData?.employeeId, employeeData)
    );
  } else {
    addEntry(shift?.employeeId, {
      employeeId: shift?.employeeId,
      breaks: shift?.breaks || [],
      overtime: shift?.overtime || [],
      absences: shift?.absences || [],
      isObemannad: shift?.isObemannad || false,
    });
  }

  const shiftId = normalizeId(shift?.originalId || shift?.id);
  bookings
    .filter(
      (booking) =>
        booking?.status === "approved" &&
        booking?.userId &&
        normalizeId(booking?.originalShiftId || booking?.shiftId) === shiftId
    )
    .forEach((booking) =>
      addEntry(booking.userId, {
        employeeId: booking.userId,
        breaks: [],
        overtime: [],
        absences: [],
        isFromBooking: true,
      })
    );

  return entries;
}

export default function OvertimeOverview() {
  const { currentUser } = useAuth();
  const { activeCompany, loading: companyLoading } = useCompany();

  const [companyRootUid, setCompanyRootUid] = useState(null);
  const [employees, setEmployees] = useState([]);
  const [overtimeRules, setOvertimeRules] = useState([]);
  const [approvedOvertimeRequests, setApprovedOvertimeRequests] = useState([]);
  const [shifts, setShifts] = useState([]);
  const [bookings, setBookings] = useState([]);
  const [loading, setLoading] = useState(true);

  const [searchQuery, setSearchQuery] = useState("");
  const [selectedMonth, setSelectedMonth] = useState("");
  const [selectedYear, setSelectedYear] = useState("");
  const [expandedEmployee, setExpandedEmployee] = useState(null);

  useEffect(() => {
    const currentDate = new Date();
    const months = [
      "Januari",
      "Februari",
      "Mars",
      "April",
      "Maj",
      "Juni",
      "Juli",
      "Augusti",
      "September",
      "Oktober",
      "November",
      "December",
    ];

    setSelectedMonth(months[currentDate.getMonth()]);
    setSelectedYear(currentDate.getFullYear().toString());
  }, []);

  const monthsInSwedish = [
    "Januari",
    "Februari",
    "Mars",
    "April",
    "Maj",
    "Juni",
    "Juli",
    "Augusti",
    "September",
    "Oktober",
    "November",
    "December",
  ];

  const years = useMemo(() => {
    const currentYearNum = new Date().getFullYear();
    const list = [];

    for (let year = 2020; year <= currentYearNum + 2; year++) {
      list.push(year.toString());
    }

    return list;
  }, []);

  useEffect(() => {
    let isMounted = true;

    const resolveCompanyRootUid = async () => {
      setLoading(true);

      if (activeCompany?.companyAccountUid) {
        if (isMounted) {
          setCompanyRootUid(activeCompany.companyAccountUid);
        }
        return;
      }

      if (!currentUser?.uid) {
        if (isMounted) {
          setCompanyRootUid(null);
          setLoading(false);
        }
        return;
      }

      try {
        const currentUserRef = doc(db, "users", currentUser.uid);
        const currentUserSnap = await getDoc(currentUserRef);

        if (!currentUserSnap.exists()) {
          if (isMounted) setCompanyRootUid(currentUser.uid);
          return;
        }

        const currentUserData = currentUserSnap.data();
        const userCompanyId = currentUserData.companyId || null;

        if (!userCompanyId) {
          if (isMounted) setCompanyRootUid(currentUser.uid);
          return;
        }

        const superAdminQuery = query(
          collection(db, "users"),
          where("companyId", "==", userCompanyId),
          where("role", "==", "super-admin"),
          limit(1)
        );

        const superAdminSnap = await getDocs(superAdminQuery);

        if (!superAdminSnap.empty) {
          if (isMounted) setCompanyRootUid(superAdminSnap.docs[0].id);
          return;
        }

        const adminQuery = query(
          collection(db, "users"),
          where("companyId", "==", userCompanyId),
          where("role", "==", "admin"),
          limit(1)
        );

        const adminSnap = await getDocs(adminQuery);

        if (!adminSnap.empty) {
          if (isMounted) setCompanyRootUid(adminSnap.docs[0].id);
          return;
        }

        if (isMounted) setCompanyRootUid(currentUser.uid);
      } catch (error) {
        console.error("Fel vid hämtning av företagets root uid:", error);
        if (isMounted) setCompanyRootUid(currentUser?.uid || null);
      }
    };

    resolveCompanyRootUid();

    return () => {
      isMounted = false;
    };
  }, [activeCompany?.companyAccountUid, currentUser?.uid]);

  useEffect(() => {
    if (!companyRootUid) {
      setEmployees([]);
      setLoading(false);
      return;
    }

    setLoading(true);

    const employedRef = collection(db, "users", companyRootUid, "employed");

    const unsubscribe = onSnapshot(
      employedRef,
      (snapshot) => {
        const users = [];

        snapshot.forEach((userDoc) => {
          const userData = userDoc.data();

          users.push({
            id: userDoc.id,
            ...userData,
            name: getEmployeeName(userData),
            email: userData.email || "",
            role: userData.role || "user",
            payroll: userData.payroll || {},
          });
        });

        users.sort((a, b) => (a.name || "").localeCompare(b.name || "", "sv-SE"));
        setEmployees(users);
        setLoading(false);
      },
      (error) => {
        console.error("Fel vid hämtning av anställda:", error);
        setEmployees([]);
        setLoading(false);
      }
    );

    return () => unsubscribe();
  }, [companyRootUid]);

  useEffect(() => {
    if (!companyRootUid) {
      setOvertimeRules([]);
      return;
    }

    const overtimeRulesRef = collection(db, "users", companyRootUid, "overtimeRules");

    const unsubscribe = onSnapshot(
      overtimeRulesRef,
      (snapshot) => {
        const rules = snapshot.docs.map((docItem) => ({
          id: docItem.id,
          ...docItem.data(),
        }));

        // Behåll Firestores ursprungliga ordning. TimeReportPopup använder
        // den första giltiga regeln som reserv när ansökan saknar procent.
        setOvertimeRules(rules);
      },
      (error) => {
        console.error("Fel vid hämtning av övertidsregler:", error);
        setOvertimeRules([]);
      }
    );

    return () => unsubscribe();
  }, [companyRootUid]);

  useEffect(() => {
    if (!companyRootUid) {
      setApprovedOvertimeRequests([]);
      return;
    }

    const overtimeRequestsRef = collection(
      db,
      "users",
      companyRootUid,
      "overtimeRequests"
    );

    const q = query(overtimeRequestsRef, where("status", "==", "approved"));

    const unsubscribe = onSnapshot(
      q,
      (snapshot) => {
        const requests = snapshot.docs.map((docItem) => ({
          id: docItem.id,
          ...docItem.data(),
        }));

        setApprovedOvertimeRequests(requests);
      },
      (error) => {
        console.error("Fel vid hämtning av godkända övertidsansökningar:", error);
        setApprovedOvertimeRequests([]);
      }
    );

    return () => unsubscribe();
  }, [companyRootUid]);


  useEffect(() => {
    if (!companyRootUid) {
      setShifts([]);
      return;
    }

    return onSnapshot(
      collection(db, "users", companyRootUid, "shifts"),
      (snapshot) => {
        setShifts(
          snapshot.docs.map((docItem) => ({ id: docItem.id, ...docItem.data() }))
        );
      },
      (error) => {
        console.error("Fel vid hämtning av pass till övertidsöversikt:", error);
        setShifts([]);
      }
    );
  }, [companyRootUid]);

  useEffect(() => {
    if (!companyRootUid) {
      setBookings([]);
      return;
    }

    return onSnapshot(
      collection(db, "users", companyRootUid, "bookings"),
      (snapshot) => {
        setBookings(
          snapshot.docs.map((docItem) => ({ id: docItem.id, ...docItem.data() }))
        );
      },
      (error) => {
        console.error("Fel vid hämtning av bokningar till övertidsöversikt:", error);
        setBookings([]);
      }
    );
  }, [companyRootUid]);

  const employeeMap = useMemo(() => {
    const map = {};

    employees.forEach((employee) => {
      map[employee.id] = employee;
    });

    return map;
  }, [employees]);

  const overtimeRuleMap = useMemo(() => {
    const map = {};

    overtimeRules.forEach((rule) => {
      map[rule.id] = rule;
    });

    return map;
  }, [overtimeRules]);

  const employeesWithRequests = useMemo(() => {
    if (!selectedMonth || !selectedYear) return [];

    const selectedMonthIndex = getMonthIndexFromSwedish(selectedMonth);
    const selectedYearNumber = Number(selectedYear);
    const grouped = new Map();

    shifts.forEach((shift) => {
      const shiftDate = safeParseDate(shift?.date);
      if (!shiftDate) return;
      if (
        shiftDate.getMonth() !== selectedMonthIndex ||
        shiftDate.getFullYear() !== selectedYearNumber
      ) {
        return;
      }

      const employeeEntries = buildEmployeeEntriesFromShift(
        shift,
        bookings,
        approvedOvertimeRequests,
        employees,
        overtimeRules
      );

      employeeEntries.forEach(({ employeeId, employeeData }) => {
        const overtime = calculateOvertimeForShift(shift, employeeData);
        if (overtime.hours <= 0) return;

        const employee = employeeMap[employeeId];
        const employeeName = employee?.name || employeeData?.name || "Okänd anställd";
        const hourlyRate = getEmployeeOvertimeHourlyRate(employee);
        const overtimeValueSEK = hourlyRate * overtime.premiumValue;

        if (!grouped.has(employeeId)) {
          grouped.set(employeeId, {
            id: employeeId,
            name: employeeName,
            requests: [],
            totalOvertimeHours: 0,
            totalOvertimePremiumHours: 0,
            totalOvertimeValueSEK: 0,
          });
        }

        const overtimeItems = Array.isArray(employeeData?.overtime)
          ? employeeData.overtime
          : [];
        const approvedItems = overtimeItems.filter((item) => item?.approvedFromRequest);
        const manualItems = overtimeItems.filter((item) => !item?.approvedFromRequest);

        const percentages = overtimeItems
          .map((item) => Number(item?.percentage || 0))
          .filter((value) => value > 0);
        const displayPercentage =
          percentages.length === 1
            ? percentages[0]
            : percentages.length > 1
              ? percentages.join("% + ")
              : 0;

        grouped.get(employeeId).requests.push({
          id: `${shift?.id || shift?.originalId}-${employeeId}`,
          date: shiftDate,
          dateFormatted: formatDateDisplay(shiftDate),
          startTimeFormatted: formatTimeDisplay(shift?.startTime),
          endTimeFormatted: formatTimeDisplay(shift?.endTime),
          overtimeRuleName:
            overtimeItems.map((item) => item?.overtimeRuleName).filter(Boolean).join(", ") ||
            "Övertid",
          percentage: displayPercentage,
          hours: overtime.hours,
          premiumValue: overtime.premiumValue,
          overtimeValueSEK,
          approvedCount: approvedItems.length,
          manualCount: manualItems.length,
          reason:
            approvedItems.map((item) => item?.reason).filter(Boolean).join("; ") || "",
        });

        const group = grouped.get(employeeId);
        group.totalOvertimeHours += overtime.hours;
        group.totalOvertimePremiumHours += overtime.hours;
        group.totalOvertimeValueSEK += overtimeValueSEK;
      });
    });

    for (const group of grouped.values()) {
      group.requests.sort(
        (a, b) => (a.date?.getTime?.() || 0) - (b.date?.getTime?.() || 0)
      );
    }

    return Array.from(grouped.values()).sort((a, b) =>
      a.name.localeCompare(b.name, "sv-SE")
    );
  }, [
    shifts,
    bookings,
    approvedOvertimeRequests,
    selectedMonth,
    selectedYear,
    employees,
    employeeMap,
    overtimeRules,
  ]);

  const filteredEmployees = useMemo(() => {
    const q = searchQuery.toLowerCase().trim();

    return employeesWithRequests.filter((employee) => {
      if (!q) return true;
      return employee.name.toLowerCase().includes(q);
    });
  }, [employeesWithRequests, searchQuery]);

  const summary = useMemo(() => {
    const totalOvertimePremiumHours = filteredEmployees.reduce(
      (sum, employee) => sum + Number(employee.totalOvertimePremiumHours || 0),
      0
    );

    const totalOvertimeValueSEK = filteredEmployees.reduce(
      (sum, employee) => sum + Number(employee.totalOvertimeValueSEK || 0),
      0
    );

    return { totalOvertimePremiumHours, totalOvertimeValueSEK };
  }, [filteredEmployees]);

  const toggleExpand = (employeeId) => {
    setExpandedEmployee(expandedEmployee === employeeId ? null : employeeId);
  };

  const styles = {
    page: {
      minHeight: "100vh",
      background: "#f6f7fb",
      padding: "22px 24px 40px",
      fontFamily:
        'Inter, ui-sans-serif, system-ui, -apple-system, BlinkMacSystemFont, "Segoe UI", sans-serif',
      color: "#1f2937",
      boxSizing: "border-box",
    },
    container: {
      width: "100%",
      maxWidth: "1400px",
      margin: "0 auto",
    },
    topRow: {
      display: "flex",
      justifyContent: "space-between",
      alignItems: "flex-start",
      gap: "16px",
      flexWrap: "wrap",
      marginBottom: "14px",
    },
    title: {
      margin: 0,
      fontSize: "42px",
      lineHeight: 1.1,
      fontWeight: 800,
      color: "#101b3a",
      letterSpacing: "-1px",
      display: "flex",
      alignItems: "center",
      gap: "16px",
    },
    titleIcon: {
      background: "linear-gradient(135deg, #f59e0b, #f97316)",
      padding: "10px",
      borderRadius: "18px",
      color: "#fff",
      display: "inline-flex",
    },
    subtitle: {
      margin: "12px 0 0",
      fontSize: "15px",
      lineHeight: 1.45,
      color: "#5f6b85",
      maxWidth: "640px",
    },
    divider: {
      height: 1,
      background: "#dde3ef",
      margin: "16px 0 18px",
      width: "100%",
    },
    summaryPanel: {
      background: "linear-gradient(135deg, #fff7ed, #ffedd5)",
      borderRadius: "14px",
      padding: "20px",
      marginBottom: "24px",
      border: "1px solid #fed7aa",
    },
    summaryTitle: {
      margin: 0,
      fontSize: "20px",
      fontWeight: 700,
      color: "#f97316",
    },
    summaryMeta: {
      margin: "6px 0 16px",
      fontSize: "14px",
      fontWeight: 600,
      color: "#f97316",
    },
    summaryGrid: {
      display: "grid",
      gridTemplateColumns: "repeat(2, minmax(0, 1fr))",
      gap: "16px",
    },
    summaryCard: {
      background: "#fff",
      border: "1px solid #fed7aa",
      borderRadius: "14px",
      padding: "18px 16px",
      display: "flex",
      flexDirection: "column",
      gap: "8px",
    },
    summaryCardLabel: {
      fontSize: "13px",
      fontWeight: 600,
      color: "#6b7280",
      textTransform: "uppercase",
      letterSpacing: "0.5px",
    },
    summaryCardValue: {
      fontSize: "28px",
      fontWeight: 800,
      color: "#111827",
    },
    filtersWrap: {
      display: "grid",
      gridTemplateColumns: "1fr 148px 148px",
      gap: "10px",
      background: "#fff",
      borderRadius: "12px",
      padding: "12px",
      marginBottom: "20px",
      border: "1px solid #e5e9f0",
    },
    searchWrap: {
      position: "relative",
      width: "100%",
    },
    searchIcon: {
      position: "absolute",
      left: "14px",
      top: "50%",
      transform: "translateY(-50%)",
      color: "#9ca3af",
      fontSize: "14px",
    },
    searchInput: {
      width: "100%",
      height: "42px",
      borderRadius: "10px",
      border: "1px solid #e2e6ed",
      padding: "0 14px 0 40px",
      fontSize: "14px",
      background: "#fff",
      color: "#111827",
      outline: "none",
      boxSizing: "border-box",
    },
    select: {
      width: "100%",
      height: "42px",
      borderRadius: "10px",
      border: "1px solid #e2e6ed",
      padding: "0 12px",
      fontSize: "14px",
      background: "#fff",
      color: "#111827",
      outline: "none",
      boxSizing: "border-box",
      cursor: "pointer",
    },
    tableWrap: {
      background: "#fff",
      border: "1px solid #e5e9f0",
      borderRadius: "14px",
      overflow: "hidden",
      boxShadow: "0 1px 3px rgba(0,0,0,0.05)",
    },
    table: {
      width: "100%",
      borderCollapse: "collapse",
      minWidth: "500px",
    },
    thead: {
      background: "#f8fafd",
    },
    th: {
      textAlign: "left",
      fontSize: "13px",
      fontWeight: 700,
      color: "#4a5568",
      padding: "14px 16px",
      borderBottom: "1px solid #e5e9f0",
    },
    td: {
      fontSize: "14px",
      color: "#1a202c",
      padding: "14px 16px",
      borderBottom: "1px solid #edf2f7",
    },
    overtimeHoursCell: {
      fontWeight: 600,
      color: "#f59e0b",
    },
    overtimeValueCell: {
      fontWeight: 600,
      color: "#10b981",
    },
    emptyState: {
      background: "#fff",
      border: "1px solid #e5e9f0",
      borderRadius: "14px",
      padding: "48px",
      textAlign: "center",
      color: "#6b7280",
    },
    expandRow: {
      cursor: "pointer",
      transition: "background 0.2s",
    },
    detailsRow: {
      background: "#fafcff",
    },
    detailsCell: {
      padding: "20px 24px",
      borderBottom: "1px solid #edf2f7",
    },
    detailsContent: {
      fontSize: "13px",
      color: "#4a5568",
      lineHeight: 1.6,
    },
    shiftItem: {
      padding: "14px",
      background: "#f8fafc",
      borderRadius: "10px",
      marginBottom: "12px",
      border: "1px solid #eef2f6",
    },
    shiftDate: {
      fontWeight: 700,
      color: "#1a202c",
      marginBottom: "8px",
      fontSize: "14px",
    },
    shiftDetails: {
      display: "grid",
      gridTemplateColumns: "repeat(auto-fit, minmax(180px, 1fr))",
      gap: "12px",
      marginTop: "10px",
    },
    overtimeTypeBadge: {
      display: "inline-block",
      padding: "2px 8px",
      borderRadius: "12px",
      fontSize: "11px",
      fontWeight: 600,
      background: "#ffedd5",
      color: "#c2410c",
      marginLeft: "8px",
    },
    requestBadge: {
      display: "inline-block",
      padding: "2px 8px",
      borderRadius: "12px",
      fontSize: "11px",
      fontWeight: 600,
      background: "#dcfce7",
      color: "#166534",
      marginLeft: "8px",
    },
  };

  if (companyLoading || loading || !selectedMonth || !selectedYear) {
    return <LoadingIndicator message="Laddar övertidsdata..." fullPage />;
  }

  if (!activeCompany && !companyRootUid) {
    return (
      <div style={styles.page}>
        <div style={styles.container}>
          <div style={styles.emptyState}>
            Inget företag valt. Vänligen logga in igen.
          </div>
        </div>
      </div>
    );
  }

  return (
    <div style={styles.page}>
      <style>{overtimeOverviewCss}</style>
      <div style={styles.container}>
        <div style={styles.topRow}>
          <div>
            <h1 style={styles.title}>
              <span style={styles.titleIcon}>
                <FaBusinessTime size={28} />
              </span>
              Övertidsöversikt
            </h1>

            <p style={styles.subtitle}>
              Översikt över all registrerad och godkänd övertid för vald period för{" "}
              <strong>{activeCompany?.companyName || "företaget"}</strong>.
              Klicka på en anställd för att se detaljerad information.
            </p>
          </div>
        </div>

        <div style={styles.divider} />

        <div style={styles.summaryPanel}>
          <h2 style={styles.summaryTitle}>SAMMANFATTNING</h2>
          <div style={styles.summaryMeta}>
            {selectedMonth.toUpperCase()} {selectedYear}
          </div>

          <div style={styles.summaryGrid}>
            <div style={styles.summaryCard}>
              <div style={styles.summaryCardLabel}>
                <FaRegClock style={{ marginRight: "6px" }} />
                Godkänd övertid
              </div>

              <div style={styles.summaryCardValue}>
                {formatHours(summary.totalOvertimePremiumHours)} h
              </div>
            </div>

            <div style={styles.summaryCard}>
              <div style={styles.summaryCardLabel}>
                <FaMoneyBillWave style={{ marginRight: "6px" }} />
                Övertidsersättning
              </div>

              <div style={styles.summaryCardValue}>
                {formatCurrency(summary.totalOvertimeValueSEK)}
              </div>
            </div>
          </div>
        </div>

        <div style={styles.filtersWrap}>
          <div style={styles.searchWrap}>
            <FaSearch style={styles.searchIcon} />
            <input
              type="text"
              placeholder="Sök efter anställd..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              style={styles.searchInput}
            />
          </div>

          <select
            value={selectedMonth}
            onChange={(e) => setSelectedMonth(e.target.value)}
            style={styles.select}
          >
            {monthsInSwedish.map((month) => (
              <option key={month} value={month}>
                {month}
              </option>
            ))}
          </select>

          <select
            value={selectedYear}
            onChange={(e) => setSelectedYear(e.target.value)}
            style={styles.select}
          >
            {years.map((year) => (
              <option key={year} value={year}>
                {year}
              </option>
            ))}
          </select>
        </div>

        {filteredEmployees.length === 0 ? (
          <div style={styles.emptyState}>
            <FaRegClock size={32} style={{ marginBottom: "12px", color: "#9ca3af" }} />
            <div>Ingen godkänd övertid hittades för vald period.</div>
            <div style={{ fontSize: "12px", marginTop: "8px", color: "#9ca3af" }}>
              Kontrollera att övertidsansökningar är godkända och har datum i vald månad.
            </div>
          </div>
        ) : (
          <div style={styles.tableWrap}>
            <div style={{ overflowX: "auto" }}>
              <table style={styles.table}>
                <thead style={styles.thead}>
                  <tr>
                    <th style={styles.th}>Namn</th>
                    <th style={styles.th}>Godkänd övertid</th>
                    <th style={styles.th}>Övertidsersättning</th>
                  </tr>
                </thead>

                <tbody>
                  {filteredEmployees.map((employee) => (
                    <React.Fragment key={employee.id}>
                      <tr
                        style={styles.expandRow}
                        onClick={() => toggleExpand(employee.id)}
                        onMouseEnter={(e) =>
                          (e.currentTarget.style.background = "#f8f9fc")
                        }
                        onMouseLeave={(e) =>
                          (e.currentTarget.style.background = "transparent")
                        }
                      >
                        <td style={styles.td}>
                          <button type="button" className="ot-person-toggle" aria-expanded={expandedEmployee === employee.id} onClick={(event) => { event.stopPropagation(); toggleExpand(employee.id); }}>
                            <FaUser size={14} color="#6b7280" />
                            {employee.name}
                            <span aria-hidden="true" style={{ marginLeft: "auto" }}>{expandedEmployee === employee.id ? "▴" : "▾"}</span>
                          </button>
                        </td>

                        <td style={{ ...styles.td, ...styles.overtimeHoursCell }}>
                          {employee.totalOvertimePremiumHours > 0
                            ? `${formatHours(employee.totalOvertimePremiumHours)} h`
                            : "-"}
                        </td>

                        <td style={{ ...styles.td, ...styles.overtimeValueCell }}>
                          {employee.totalOvertimeValueSEK > 0
                            ? formatCurrency(employee.totalOvertimeValueSEK)
                            : "-"}
                        </td>
                      </tr>

                      {expandedEmployee === employee.id && (
                        <tr style={styles.detailsRow}>
                          <td colSpan="3" style={styles.detailsCell}>
                            <div style={styles.detailsContent}>
                              <div className="ot-person-heading">
                                <strong>Övertid för {employee.name}</strong>
                                <span>{selectedMonth} {selectedYear} · {employee.requests.length} pass</span>
                              </div>
                              <p className="ot-help">Öppna ”Visa detaljer” för att se övertidstillägg och information om passet.</p>
                              <div className="ot-shift-list">
                                {employee.requests.map((request, index) => (
                                  <article key={`${request.id}-${index}`} className="ot-shift-card">
                                    <div className="ot-shift-summary">
                                      <div>
                                        <strong>{request.dateFormatted}</strong>
                                        <span className="ot-muted">Pass {request.startTimeFormatted}–{request.endTimeFormatted}</span>
                                      </div>
                                      <div><span className="ot-muted">Övertid</span><span>{formatHours(request.hours)} h</span></div>
                                      <div className="ot-shift-amount"><span className="ot-muted">Övertidsersättning</span><strong>{formatCurrency(request.overtimeValueSEK)}</strong></div>
                                    </div>
                                    <details className="ot-calculation">
                                      <summary><span className="ot-show">Visa detaljer</span><span className="ot-hide">Dölj detaljer</span></summary>
                                      <dl className="ot-detail-grid">
                                        <div><dt>Typ av övertid</dt><dd>{request.overtimeRuleName}</dd></div>
                                        <div><dt>Övertidstillägg</dt><dd>{request.percentage}% extra</dd></div>
                                        {request.approvedCount > 0 && <div><dt>Godkända via ansökan</dt><dd>{request.approvedCount} tillägg</dd></div>}
                                        {request.manualCount > 0 && <div><dt>Manuellt registrerade</dt><dd>{request.manualCount} tillägg</dd></div>}
                                        {request.startDateFormatted && <div><dt>Startdatum</dt><dd>{request.startDateFormatted}</dd></div>}
                                        {request.endDateFormatted && <div><dt>Slutdatum</dt><dd>{request.endDateFormatted}</dd></div>}
                                        {request.submittedAtFormatted && <div><dt>Skapad/skickad</dt><dd>{request.submittedAtFormatted}</dd></div>}
                                        {request.approvedAtFormatted && <div><dt>Godkänd/uppdaterad</dt><dd>{request.approvedAtFormatted}</dd></div>}
                                      </dl>
                                      {(request.reason || request.description || request.comment) && <p className="ot-help"><strong>Anledning: </strong>{request.reason || request.description || request.comment}</p>}
                                      {(request.reviewComment || request.approvalComment || request.adminComment) && <p className="ot-help"><strong>Kommentar: </strong>{request.reviewComment || request.approvalComment || request.adminComment}</p>}
                                    </details>
                                  </article>
                                ))}
                              </div>
                            </div>
                          </td>
                        </tr>
                      )}
                    </React.Fragment>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
